import { useMemo } from "react";
import { Card, Flex, Grid, HelpIndicator, QueryLoader, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { useGetTaxReportingDataQuery } from "~/graphql/supergraphHooks";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { getColumnDefinitions } from "./columnDefinitions";
import { headerContainer, tooltipContent, tooltipParagraph } from "./styles_vanilla.css";
import { DocumentInfo, FatcaCrsRowItem } from "./types";
import { transformToRowData } from "./utils";

const TOOLTIP_CONTENT = (
  <Flex flexDirection="column" gap={Sizes.MD} className={tooltipContent}>
    <Text variant="regular-md" className={tooltipParagraph}>
      This section displays individuals identified as Substantial US Owners (FATCA) or
      Controlling Persons (CRS) based on the investor&apos;s tax document.
    </Text>
    <Text variant="regular-md" className={tooltipParagraph}>
      Profiles can be added or modified in the Documents tab within the FATCA or CRS details
      section of the document.
    </Text>
  </Flex>
);

export type FatcaCrsProps = {
  documents: DocumentInfo[];
};

export const FatcaCrs = ({ documents }: FatcaCrsProps) => {
  const { caseId } = useCurrentCaseContext();

  const taxReportingResult = useGetTaxReportingDataQuery({
    variables: { caseId },
    skip: !caseId || caseId <= 0,
    fetchPolicy: "network-only",
  });

  const rowData: FatcaCrsRowItem[] = useMemo(() => {
    const taxReportingData = taxReportingResult.data?.taxReportingData ?? [];
    return transformToRowData(taxReportingData);
  }, [taxReportingResult.data?.taxReportingData]);

  const columnDefs = useMemo(() => {
    return getColumnDefinitions({ caseId, documents });
  }, [caseId, documents]);

  return (
    <Card>
      <Flex className={headerContainer} alignItems="center" gap={Sizes.XS}>
        <Text variant="secondary-md">FATCA SUBSTANTIAL OWNERS/CRS CONTROLLING PERSONS</Text>
        <HelpIndicator id="fatca-crs-help" placement="top" fullWidth content={TOOLTIP_CONTENT}>
          <Icon type="info-circle" variant="outline" />
        </HelpIndicator>
      </Flex>
      <QueryLoader result={taxReportingResult}>
        <Grid<FatcaCrsRowItem>
          columnDefs={columnDefs}
          rowData={rowData}
          gridOptions={{
            rowHeight: 46,
            headerHeight: 48,
            autoSizeStrategy: {
              type: "fitGridWidth",
            },
            domLayout: "autoHeight",
            suppressCellFocus: true,
          }}
          reactiveCustomComponents
        />
      </QueryLoader>
    </Card>
  );
};
